import React from 'react';
import { useRouter } from '../context/RouterContext';

const Privacy: React.FC = () => {
  const { navigate } = useRouter();

  const sectionClass = "text-gray-700 dark:text-slate-300";
  const headingClass = "text-gray-900 dark:text-white";

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-3 py-6">
        <button
          onClick={() => navigate('home')}
          className="flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl shadow-soft dark:shadow-soft-dark p-6 border border-gray-200/50 dark:border-slate-700/50">
          <h1 className={`text-3xl font-bold ${headingClass} mb-4`}>Privacy Policy</h1>
          
          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              <strong>Last updated:</strong> October 16, 2025
            </p>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>1. Information We Collect</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p><strong>User-Generated Content:</strong> We collect license plate images and associated metadata (such as state information) that you voluntarily upload to our platform.</p>
                <p><strong>Usage Data:</strong> We automatically collect certain information about your use of our service, including:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Voting and reaction data (anonymized)</li>
                  <li>Browser fingerprinting data for user identification</li>
                  <li>Device and browser information</li>
                  <li>IP addresses and general location data</li>
                  <li>Usage patterns and interaction data</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>2. How We Use Your Information</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We use the information we collect to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Provide and maintain our service</li>
                  <li>Prevent spam and abuse through user identification</li>
                  <li>Display voting results and user interactions</li>
                  <li>Improve our service and user experience</li>
                  <li>Ensure fair voting practices (one vote per user per day)</li>
                  <li>Analyze usage patterns to enhance functionality</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>3. Information Sharing and Disclosure</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p><strong>We do not sell, trade, or rent your personal information to third parties.</strong> We may share your information only in the following circumstances:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Public Content:</strong> License plate images and votes are displayed publicly as part of our service</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                  <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of our business</li>
                </ul>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>4. Data Storage and Security</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.</p>
                <p>Your data is stored using industry-standard cloud services with appropriate security measures in place.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>5. User Identification and Fingerprinting</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>To prevent spam and ensure fair voting, we use browser fingerprinting technology to create unique identifiers for each user. This helps us prevent duplicate votes, maintain voting integrity, and protect against automated abuse. This fingerprinting does not collect personally identifiable information.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>6. Cookies and Local Storage</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We use browser local storage to remember your voting history, store your unique user identifier, and maintain your preferences. You can clear your browser's local storage at any time, but this may affect your ability to vote on previously voted plates.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>7. Your Rights and Choices</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>You have the right to request access, correction, deletion, object to processing, and withdraw consent. To exercise these rights, please contact us using the information in the "Contact Us" section below.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>8. Children's Privacy</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>9. Changes to This Privacy Policy</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>10. Contact Us</h2>
              <div className={`space-y-4 ${sectionClass}`}>
                <p>If you have any questions about this Privacy Policy, please contact us:</p>
                <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-xl">
                  <p><strong>Email:</strong> r8mypl8@gmail.com</p>
                  <p><strong>Website:</strong> <a href="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">r8mypl8.net</a></p>
                </div>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
              <p className="text-sm text-gray-500 dark:text-slate-500">
                This Privacy Policy is effective as of the date listed above and will remain in effect except with respect to any changes in its provisions in the future.
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-500 mt-2">© 2025 Rate My Plate. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
